const SCROLL_SPEED_PX_PER_SECOND = 100;

export default class ProductNewsTicker {
    constructor() {
        this.el           = document.querySelector('.js-product-news-ticker');
        this.track        = this.el?.querySelector('.product-news-ticker__track');
        this.slides       = Array.from(this.el?.querySelectorAll('.product-news-ticker__slide') ?? []);
        this.dots         = Array.from(this.el?.querySelectorAll('.product-news-ticker__dot') ?? []);
        this.pauseBtn     = this.el?.querySelector('.js-ticker-pause');
        this.labelPause   = this.pauseBtn?.dataset.labelPause ?? 'Pause ticker';
        this.labelPlay    = this.pauseBtn?.dataset.labelPlay  ?? 'Play ticker';
        this.currentIndex = 0;
        this.animation    = null;
        this.paused       = false;
        this.slideWidths  = [];
    }

    init() {
        if (!this.el || !this.slides.length || !this.track) return;

        this._preMeasureSlideWidths();
        this._adjustBodyPadding();
        this._bindEvents();
        this._animateSlide(0);
    }

    _preMeasureSlideWidths() {
        this.slides.forEach((s) => {
            s.style.visibility = 'hidden';
            s.style.display    = 'flex';
            s.style.transform  = 'translateX(-99999px)';
        });

        this.slideWidths = this.slides.map((s) => s.offsetWidth);

        this.slides.forEach((s) => {
            s.style.display    = 'none';
            s.style.visibility = '';
            s.style.transform  = '';
        });
    }

    _animateSlide(index) {
        const slide      = this.slides[index];
        const slideWidth = this.slideWidths[index];
        const trackWidth = this.track.offsetWidth;
        const duration   = ((trackWidth + slideWidth) / SCROLL_SPEED_PX_PER_SECOND) * 1000;

        slide.style.display = 'flex';

        this.animation = slide.animate(
            [
                { transform: `translateX(${trackWidth}px)` },
                { transform: `translateX(${-slideWidth}px)` },
            ],
            { duration, easing: 'linear', fill: 'forwards' }
        );

        this._updateActiveSlide(index);

        this.animation.onfinish = () => {
            this.animation.cancel();
            slide.style.display = 'none';

            if (!this.paused) {
                this._animateSlide((index + 1) % this.slides.length);
            }
        };
    }

    _jumpToSlide(index) {
        if (index === this.currentIndex) return;

        this.animation?.cancel();
        this.slides[this.currentIndex].style.display = 'none';

        this._animateSlide(index);
    }

    _pause() {
        this.paused = true;
        this.animation?.pause();
        this.pauseBtn?.setAttribute('aria-pressed', 'true');
        this.pauseBtn?.setAttribute('aria-label', this.labelPlay);
    }

    _play() {
        this.paused = false;
        this.pauseBtn?.setAttribute('aria-pressed', 'false');
        this.pauseBtn?.setAttribute('aria-label', this.labelPause);

        if (this.animation?.playState === 'paused') {
            this.animation.play();
        } else {
            this.animation?.cancel();
            this.slides[this.currentIndex].style.display = 'none';
            this._animateSlide((this.currentIndex + 1) % this.slides.length);
        }
    }

    _togglePause() {
        this.paused ? this._play() : this._pause();
    }

    _updateActiveSlide(index) {
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

        this.slides.forEach((slide, i) => {
            const isActive = i === index;
            slide.setAttribute('aria-hidden', String(!isActive));
            slide.querySelector('a')?.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.currentIndex = index;
    }

    _bindEvents() {
        this._bindDotNavigation();
        this._bindPauseButton();
        this._bindHoverPause();
        this._bindFocusPause();
    }

    _bindDotNavigation() {
        this.dots.forEach((dot, i) => {
            dot.addEventListener('click', () => this._jumpToSlide(i));
        });
    }

    _bindPauseButton() {
        this.pauseBtn?.addEventListener('click', () => this._togglePause());

        this.pauseBtn?.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                this._togglePause();
            }
        });
    }

    _bindHoverPause() {
        this.el.addEventListener('mouseenter', () => {
            if (!this.paused) this.animation?.pause();
        });
        this.el.addEventListener('mouseleave', () => {
            if (!this.paused) this.animation?.play();
        });
    }

    _bindFocusPause() {
        this.el.addEventListener('focusin', () => {
            if (!this.paused) this.animation?.pause();
        });
        this.el.addEventListener('focusout', (e) => {
            if (!this.paused && !this.el.contains(e.relatedTarget)) {
                this.animation?.play();
            }
        });
    }

    _adjustBodyPadding() {
        const height = this.el.offsetHeight;
        if (height > 0) {
            const current = parseInt(getComputedStyle(document.body).paddingBottom || '0', 10);
            document.body.style.paddingBottom = Math.max(current, height) + 'px';
        }
    }
}
